// PATH: src/app/publicpages/Home.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuthContext } from "app/contexts/auth/context";
import { useAuthModalContext } from "app/contexts/authModal/context";
import "./Home.scss";
import EntryAnimation from "./components/EntryAnimation";
import { loadVisitorCount } from "utils/siteVisit";

// stays true for the rest of the page session, resets on a full reload
let introPlayed = false;

const THEME_KEY = "RAAJNEETI_LANDING_THEME";
const NAVY_DEFAULT_KEY = "RAAJNEETI_NAVY_DEFAULT_V1";

// navy for everyone on the first visit with this version, after that the saved choice wins
const readInitialTheme = () => {
  try {
    if (!localStorage.getItem(NAVY_DEFAULT_KEY)) return "navy";
    return localStorage.getItem(THEME_KEY) === "light" ? "light" : "navy";
  } catch {
    return "navy";
  }
};

// eases a number up from 0 to its target
const useCountUp = (target, ms = 1200) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / ms, 1);
      setValue(Math.round(target * (1 - (1 - p) ** 4)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return value;
};

const PUB_QUOTES = [
  {
    q: "Man is by nature a political animal.",
    a: "Aristotle · Politics, c. 350 BC",
  },
  {
    q: "Government of the people, by the people, for the people.",
    a: "Abraham Lincoln · Gettysburg Address, 1863",
  },
  {
    q: "In the happiness of the subjects lies the happiness of the king.",
    a: "Kautilya · Arthashastra, ancient India",
  },
  {
    q: "Constitutional morality is not a natural sentiment; it has to be cultivated.",
    a: "B.R. Ambedkar · Constituent Assembly Debates, 1948",
  },
];

// ══════ THE RING — 7 tools, in the same order/copy/tags as the POC ══════
// "Know Your Constitution" / "How Would They See It" now open the real
// /just-curious page. "Notes" / "MCQs" still fall back to /raajneeti/psir —
// confirm the exact route for those once the PSIR content pages are wired.
const RING_CARDS = [
  {
    icon: "ti-notes",
    tone: "t-psir",
    tag: "Learning PSIR",
    title: "Notes",
    desc: "Comprehensive notes. Complete preparation.",
    dest: "/raajneeti/psir", // TODO: confirm exact "Notes" route
  },
  {
    icon: "ti-list-check",
    tone: "t-psir",
    tag: "Learning PSIR",
    title: "MCQs",
    desc: "Practice sets filed by topic, with the reasoning shown for every option.",
    dest: "/raajneeti/psir", // TODO: confirm exact "MCQs" route
  },
  {
    icon: "ti-target",
    tone: "t-psir",
    tag: "Learning PSIR",
    title: "AI समीक्षा",
    desc: "Compare your answer with a topper’s, get a score, and personalized feedback.",
    dest: "/raajneeti/ai-samiksha",
  },
  {
    icon: "ti-building-bank",
    tone: "t-civic",
    tag: "Just Curious",
    title: "Know Your Constitution",
    desc: "Any article, in plain language — what it says and where it bites.",
    dest: "/just-curious?tool=const",
  },
  {
    icon: "ti-users",
    tone: "t-civic",
    tag: "Just Curious",
    title: "How Would They See It",
    desc: "Three thinkers read the same headline, and disagree in front of you.",
    dest: "/raajneeti/current-affairs?tab=hwt",
  },
  {
    icon: "ti-news",
    tone: "t-track",
    tag: "GS Current Affairs",
    title: "GS Current Affairs",
    desc: "Today's headlines filed to the syllabus topic each one strengthens.",
    dest: "/gs-current-affairs",
    soon: true,
  },
  {
    icon: "ti-article",
    tone: "t-psir",
    tag: "PSIR Current Tracker",
    title: "Daily Editorials Decoded",
    desc: "The day's editorials split into argument, syllabus linkage and practice questions.",
    dest: "/raajneeti/current-affairs?tab=daily",
  },
];
const FX_N = RING_CARDS.length;
const FX_DWELL = 2400;

// ══════ WHY RAAJNEETI — comparison table, same copy as POC ══════
const COMPARE_ROWS = [
  {
    icon: "ti-target",
    label: "Answer Evaluation",
    coaching: "Manual — turnaround in days",
    ai: "No real topper benchmark",
    us: "Named against a real topper's actual answer, instantly",
  },
  {
    icon: "ti-books",
    label: "Optional-Specific Depth",
    coaching: "Generalist GS focus",
    ai: "No syllabus mapping",
    us: "Every topic mapped to the real PSIR syllabus",
  },
  {
    icon: "ti-world",
    label: "Civic Literacy Tools",
    coaching: "Not offered",
    ai: "Not curated for India",
    us: "Free public tools — no UPSC angle required",
  },
  {
    icon: "ti-news",
    label: "Current Tracker",
    coaching: "Monthly PDF dumps, weeks behind the news",
    ai: "Unsourced summaries, nothing tied to the syllabus",
    us: "Every headline filed to its GS topic, with same-day MCQs",
  },
  {
    icon: "ti-gavel",
    label: "Government & Legal Sourcing",
    coaching: "Second-hand, often stale",
    ai: "Can fabricate citations",
    us: "Every article and government's action independently verified",
  },
];

export default function Home() {
  const navigate = useNavigate();

  const [quoteIdx, setQuoteIdx] = useState(0);
  const [quoteVisible, setQuoteVisible] = useState(true);
  const [theme, setTheme] = useState(readInitialTheme); // 'light' | 'navy'
  const [introDone, setIntroDone] = useState(introPlayed);
  const [visitorToday, setVisitorToday] = useState(null);
  const todayShown = useCountUp(visitorToday, 1000);

  // ---- footer visitor count (today only) ----
  useEffect(() => loadVisitorCount((n) => setVisitorToday(n)), []);

  // ---- quote rotation ----
  useEffect(() => {
    const tick = () => {
      setQuoteVisible(false);
      setTimeout(() => {
        setQuoteIdx((i) => (i + 1) % PUB_QUOTES.length);
        setQuoteVisible(true);
      }, 250);
    };
    setQuoteVisible(true);
    const id = setInterval(tick, 6000);
    return () => clearInterval(id);
  }, []);

  // ---- theme switcher (Light / Navy), persisted ----
  useEffect(() => {
    document.body.classList.toggle("theme-navy", theme === "navy");
    try {
      localStorage.setItem(THEME_KEY, theme);
      localStorage.setItem(NAVY_DEFAULT_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    return () => document.body.classList.remove("theme-navy");
  }, [theme]);

  const { isAuthenticated } = useAuthContext();
  const { openAuthModal } = useAuthModalContext();

  const enterApp = () => {
    if (isAuthenticated) {
      navigate("/raajneeti/home");
    } else {
      openAuthModal({
        label: "Learning PSIR",
        onSuccess: () => navigate("/raajneeti/home"),
      });
    }
  };
  const exitApp = () => navigate("/");
  const goExplore = () => navigate("/just-curious");
  const goDailyTracker = () => navigate("/gs-current-affairs");
  const goSamiksha = () => navigate("/raajneeti/ai-samiksha");

  // ---- THE RING carousel ----
  const [fxIdx, setFxIdx] = useState(0);
  const [fxInView, setFxInView] = useState(false);
  const [fxPaused, setFxPaused] = useState(false);
  const fxStageRef = useRef(null);

  useEffect(() => {
    const stage = fxStageRef.current;
    if (!stage || !window.IntersectionObserver) {
      setFxInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => setFxInView(entries[0].isIntersecting),
      { threshold: 0.25 },
    );
    io.observe(stage);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const calm =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (calm || !fxInView || fxPaused) return;
    const timer = setInterval(() => {
      setFxIdx((i) => (i + 1) % FX_N);
    }, FX_DWELL);
    return () => clearInterval(timer);
  }, [fxInView, fxPaused]);

  const fxGo = useCallback((i) => {
    setFxIdx(((i % FX_N) + FX_N) % FX_N);
  }, []);
  const fxStep = (dir) => fxGo(fxIdx + dir);

  const handleFxCard = (i) => {
    if (i === fxIdx) {
      if (RING_CARDS[i].soon) return;
      navigate(RING_CARDS[i].dest);
      window.scrollTo({ top: 0, behavior: "auto" });
    } else {
      fxGo(i);
    }
  };

  const quote = PUB_QUOTES[quoteIdx];

  return (
    <div className="home-wrapper">
      {!introDone && (
        <EntryAnimation
          onDone={() => {
            introPlayed = true;
            setIntroDone(true);
          }}
        />
      )}
      <link
        href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.10.0/dist/tabler-icons.min.css"
      />

      <nav className="pub-nav" id="pub-nav">
        <div className="pub-nav-brand" onClick={exitApp}>
          Raajneeti <b>AI</b>
        </div>
        <div className="pub-nav-links">
          <button onClick={goExplore}>Explore</button>
          {/* <button type="button" className="pub-nav-soon" aria-disabled="true">
            GS Current Affairs{" "}
            <span className="nav-soon-pill">Coming soon</span>
          </button> */}
          <button className="pub-nav-ai" onClick={goSamiksha}>
            <i className="ti ti-sparkles"></i> AI समीक्षा
          </button>
        </div>
        <button className="pub-nav-cta" onClick={enterApp}>
          Learning PSIR <i className="ti ti-arrow-right"></i>
        </button>
      </nav>

      <div id="pg-home" className="pg on">
        {/* theme preview switcher */}
        <div className="theme-switch">
          <span>Theme</span>
          <button
            className={`ts-btn ${theme === "light" ? "on" : ""}`}
            onClick={() => setTheme("light")}
          >
            Light
          </button>
          <button
            className={`ts-btn ${theme === "navy" ? "on" : ""}`}
            onClick={() => setTheme("navy")}
          >
            Navy
          </button>
        </div>

        <div className="hw">
          <div className="hi-split">
            <div className="hi-left">
              <div className="brand-lockup">
                <div className="rj-wordmark">
                  Raajneeti<span className="rj-ai">ai</span>
                </div>
              </div>
              <div className="raajneeti-tag2">Build Political Intelligence</div>

              <h1 className="hh1">
                Everyone follows politics.
                <br />
                <em>Not everyone knows how to think about it.</em>
              </h1>
              <p className="pub-sub">
                Political literacy for every section of society — AI-powered,
                expert-curated by mentors who have taught political science for
                20+ years, so students, citizens, and institutions alike can
                understand politics, not just consume it.
              </p>

              <div className="quote-strip" id="quote-strip">
                <svg className="quote-mark" viewBox="0 0 24 24">
                  <path d="M7 7c-2.2 1.4-3.5 3.4-3.5 6 0 2.2 1.4 3.8 3.3 3.8 1.7 0 3-1.3 3-3 0-1.5-1-2.6-2.4-2.8.3-1.6 1.5-2.9 3.1-3.6L7 7zm10 0c-2.2 1.4-3.5 3.4-3.5 6 0 2.2 1.4 3.8 3.3 3.8 1.7 0 3-1.3 3-3 0-1.5-1-2.6-2.4-2.8.3-1.6 1.5-2.9 3.1-3.6L17 7z" />
                </svg>
                <div
                  className="quote-text"
                  id="quote-text"
                  style={{ opacity: quoteVisible ? 1 : 0 }}
                >
                  {`"${quote.q}"`}
                </div>
                <div className="quote-author" id="quote-author">
                  {quote.a}
                </div>
              </div>
            </div>

            <div className="hi-right">
              <div className="startbox">
                <div className="sfb-top">
                  <div className="sfb-rule"></div>
                  <div className="pub-section-lbl hi-right-lbl">
                    <i className="ti ti-compass"></i> Choose your way in
                  </div>
                </div>
                <div className="sfb-div"></div>
                <button className="sfb-row" onClick={enterApp}>
                  <span className="sfb-ico">🎓</span>
                  <div className="sfb-body">
                    <h3>Learning PSIR</h3>
                    <p className="sfb-vibe">
                      Structured preparation for UPSC PSIR. One topic at a time.
                    </p>
                    <div className="sfb-cta">
                      Start Learning <i className="ti ti-arrow-right"></i>
                    </div>
                  </div>
                </button>
                <div className="sfb-div"></div>
                <button className="sfb-row sfb-ai" onClick={goSamiksha}>
                  <span className="sfb-ico">✨</span>
                  <div className="sfb-body">
                    <h3>AI समीक्षा</h3>
                    <p className="sfb-vibe">
                      Compare your answer with a topper’s, get a score, and
                      personalized feedback.
                    </p>
                    <div className="sfb-cta">
                      Evaluate my answer <i className="ti ti-arrow-right"></i>
                    </div>
                  </div>
                </button>
                <div className="sfb-div"></div>
                <button className="sfb-row" onClick={goExplore}>
                  <span className="sfb-ico">🌍</span>
                  <div className="sfb-body">
                    <h3>Just Curious</h3>
                    <p className="sfb-vibe">
                      Understand the Constitution &amp; politics without the
                      jargon.
                    </p>
                    <div className="sfb-cta">
                      Explore <i className="ti ti-arrow-right"></i>
                    </div>
                  </div>
                </button>
                <div className="sfb-div"></div>
                <div className="sfb-row soon" aria-disabled="true">
                  <span className="soon-badge">Coming soon</span>
                  <span className="sfb-ico">📰</span>
                  <div className="sfb-body">
                    <h3>GS Current Affairs</h3>
                    <p className="sfb-vibe">
                      Every headline, decoded against the UPSC syllabus.
                    </p>
                    <div className="sfb-cta">
                      See today&rsquo;s brief and practice MCQs{" "}
                      <i className="ti ti-arrow-right"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ══════ THE RING — live 3D carousel of every tool already built ══════ */}
        <section className="fx-section">
          <div className="fx-inner">
            <div
              className="pub-section-lbl"
              style={{ justifyContent: "center", marginBottom: 6 }}
            >
              <i className="ti ti-rotate-clockwise"></i> What's inside
            </div>
            <h2 className="fx-h2">Seven tools, already built</h2>
            <p className="fx-sub">
              Not a roadmap — every face below already works. Pick any card to
              bring it forward.
            </p>

            <div
              className="fx-stage"
              id="fx-stage"
              ref={fxStageRef}
              onMouseEnter={() => setFxPaused(true)}
              onMouseLeave={() => setFxPaused(false)}
              onFocus={() => setFxPaused(true)}
              onBlur={() => setFxPaused(false)}
            >
              <div
                className="fx-ring"
                id="fx-ring"
                style={{
                  transform: `translateZ(calc(var(--fx-r) * -1)) rotateY(${-fxIdx * (360 / FX_N)}deg)`,
                }}
              >
                {RING_CARDS.map((card, i) => {
                  let d = (((i - fxIdx) % FX_N) + FX_N) % FX_N;
                  if (d > FX_N / 2) d -= FX_N;
                  const ad = Math.abs(d);
                  const isFront = ad === 0;
                  return (
                    <button
                      key={card.title}
                      className={`fx-card ${isFront ? "is-front" : ""} ${card.soon ? "is-soon" : ""}`}
                      style={{
                        "--i": i,
                        opacity: ad === 0 ? 1 : ad === 1 ? 0.42 : 0,
                        pointerEvents: ad <= 1 ? "auto" : "none",
                      }}
                      aria-hidden={ad > 1}
                      tabIndex={ad > 1 ? -1 : 0}
                      onClick={() => handleFxCard(i)}
                    >
                      {card.soon && (
                        <span className="soon-badge">Coming soon</span>
                      )}
                      <span className={`fx-ico ${card.tone}`}>
                        <i className={`ti ${card.icon}`}></i>
                      </span>
                      <h3>{card.title}</h3>
                      <p>{card.desc}</p>
                      <span className="fx-foot">
                        <span className={`fx-tag ${card.tone}`}>
                          {card.tag}
                        </span>
                        {!card.soon && (
                          <span className="fx-open">
                            Open <i className="ti ti-arrow-right"></i>
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="fx-controls">
              <button
                className="fx-arrow"
                onClick={() => fxStep(-1)}
                aria-label="Previous tool"
              >
                <i className="ti ti-chevron-left"></i>
              </button>
              <div className="fx-dots" id="fx-dots">
                {RING_CARDS.map((card, i) => (
                  <button
                    key={card.title}
                    className={`fx-dot ${i === fxIdx ? "on" : ""}`}
                    aria-label={`Show ${card.title}`}
                    onClick={() => fxGo(i)}
                  ></button>
                ))}
              </div>
              <button
                className="fx-arrow"
                onClick={() => fxStep(1)}
                aria-label="Next tool"
              >
                <i className="ti ti-chevron-right"></i>
              </button>
            </div>
          </div>
        </section>

        {/* ══════ WHY RAAJNEETI ══════ */}
        <div className="rj-compare-section">
          <div className="rj-compare-inner">
            <div
              className="pub-section-lbl"
              style={{ textAlign: "center", marginBottom: 6 }}
            >
              <i className="ti ti-scale"></i> Why Raajneeti AI
            </div>
            <h2 className="rjc-h2">What actually sets it apart</h2>
            <p className="rjc-sub">
              Not from any one competitor — from generic AI chatbots and
              traditional coaching alike.
            </p>

            <div className="rj-compare-table">
              <div className="rjc-row rjc-head">
                <div className="rjc-cell rjc-label"></div>
                <div className="rjc-cell">Traditional Coaching</div>
                <div className="rjc-cell">Generic AI Chatbots</div>
                <div className="rjc-cell rjc-us">Raajneeti AI</div>
              </div>
              {COMPARE_ROWS.map((row) => (
                <div className="rjc-row" key={row.label}>
                  <div className="rjc-cell rjc-label">
                    <i className={`ti ${row.icon}`}></i> {row.label}
                  </div>
                  <div className="rjc-cell rjc-weak">{row.coaching}</div>
                  <div className="rjc-cell rjc-weak">{row.ai}</div>
                  <div className="rjc-cell rjc-us">{row.us}</div>
                </div>
              ))}
            </div>
            <p className="rjc-foot-note">
              Every claim above describes a tool already built into this
              platform, not a promise for later.
            </p>
          </div>
        </div>

        {/* ══════ FOOTER ══════ */}
        <footer className="rj-footer">
          <div className="rjf-top">
            <div className="rjf-brand">
              <div className="rjf-brandrow">
                <div className="rjf-wordmark">
                  Raajneeti<span className="rj-ai">ai</span>
                </div>
                {visitorToday !== null && (
                  <div
                    className="rjf-visitors"
                    title="Visitors today on Raajneeti AI"
                  >
                    <span className="rjf-live-dot"></span>
                    <span className="rjf-vis-item">
                      {/* ghost holds the final width so the pill never
                          resizes while the number counts up */}
                      <span className="rjf-num">
                        <span className="rjf-num-ghost" aria-hidden="true">
                          {visitorToday.toLocaleString("en-IN")}
                        </span>
                        <b>{todayShown.toLocaleString("en-IN")}</b>
                      </span>
                      <span className="rjf-vis-label">
                        {visitorToday === 1 ? "visitor" : "visitors"} today
                      </span>
                    </span>
                  </div>
                )}
              </div>
              <p>
                AI-powered political literacy and UPSC PSIR preparation
                platform.
              </p>

              <div className="rjf-trust">
                <div className="rjf-trust-item">
                  <div className="rjf-trust-ico">
                    <i className="ti ti-school"></i>
                  </div>
                  <div className="rjf-trust-txt">
                    <strong>Expert Mentors</strong>
                    <span>20+ Years of Teaching</span>
                  </div>
                </div>
                <div className="rjf-trust-item">
                  <div className="rjf-trust-ico">
                    <i className="ti ti-book"></i>
                  </div>
                  <div className="rjf-trust-txt">
                    <strong>Curated Content</strong>
                    <span>Reliable. Relevant. Updated.</span>
                  </div>
                </div>
                <div className="rjf-trust-item">
                  <div className="rjf-trust-ico">
                    <i className="ti ti-users"></i>
                  </div>
                  <div className="rjf-trust-txt">
                    <strong>For Everyone</strong>
                    <span>Students. Citizens. Institutions.</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rjf-col">
              <h4>Explore</h4>
              <a onClick={enterApp}>Learning PSIR</a>
              <a onClick={goSamiksha}>AI समीक्षा </a>
              <a onClick={goExplore}>Just Curious</a>
              {/* <a onClick={goDailyTracker}>GS Current Affairs</a> */}
              <a onClick={() => navigate("/just-curious?tool=const")}>
                Know Your Constitution
              </a>
            </div>
            <div className="rjf-col">
              <h4>Company</h4>
              <a>About Us</a>
              <a>Our Mentors</a>
              <a>Help &amp; Support</a>
            </div>
            <div className="rjf-col">
              <h4>Contact Us</h4>
              <p className="rjf-lbl">Student Queries</p>
              <a href="mailto:Support@raajneeti.ai">Support@raajneeti.ai</a>
              <p className="rjf-lbl">General Queries</p>
              <a href="mailto:Enquiry@raajneeti.ai">Enquiry@raajneeti.ai</a>
            </div>
          </div>
          {/* <div className="rjf-bottom">
            <span>© Raajneeti AI</span>
            <span className="rjf-legal">
              <a>Terms</a> · <a>Privacy Policy</a>
            </span>
          </div> */}
        </footer>
      </div>
    </div>
  );
}